import { type RefObject, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';

import { usePatientCreateMutation } from '~/api/query/patientCreate';
import { usePatientSearchVerifyMutation } from '~/api/query/patientSearchVerify';
import type { PatientSearchVerifyResponseFound } from '~/api/types';

import { PatientCreateSteps } from './constants';
import type {
  PatientCreationFormState,
  PatientInformationFormValues,
  PatientIntroFormRef,
  PatientIntroFormValues,
  PatientSettingsFormRef,
} from './types';
import { useModal } from '../modals/useModal';

const formAtom = atom<PatientCreationFormState>({
  step: PatientCreateSteps.PATIENT_INTRO,
  isNextStepDisabled: false,
  values: {},
});

const selectedFoundPatientIdAtom = atom<string>();

const foundPatientsAtom = atom<
  [PatientSearchVerifyResponseFound] | PatientSearchVerifyResponseFound[]
>();

type UsePatientCreationFormOptions = {
  introFormRef: RefObject<PatientIntroFormRef | null>;
  settingsFormRef: RefObject<PatientSettingsFormRef | null>;
  informationFormRef: RefObject<PatientIntroFormRef | null>;
};

export const usePatientCreationForm = ({
  introFormRef,
  settingsFormRef,
  informationFormRef,
}: UsePatientCreationFormOptions) => {
  const [{ step, isNextStepDisabled, values }, setFormState] =
    useAtom(formAtom);
  const [selectedFoundPatientId, setSelectedFoundPatientId] = useAtom(
    selectedFoundPatientIdAtom,
  );
  const [foundPatients, setFoundPatients] = useAtom(foundPatientsAtom);
  const { openModal, closeModal } = useModal();
  const patientSearchVerifyMutation = usePatientSearchVerifyMutation();
  const patientCreateMutation = usePatientCreateMutation();

  const queryClient = useQueryClient();

  const setIsNextStepDisabled = useCallback(
    (value: boolean) => {
      setFormState((prevState) => ({
        ...prevState,
        isNextStepDisabled: value,
      }));
    },
    [setFormState],
  );

  const selectFoundPatient = useCallback(
    (patientId: string) => {
      setSelectedFoundPatientId(patientId);
      setIsNextStepDisabled(false);
    },
    [setIsNextStepDisabled, setSelectedFoundPatientId],
  );

  const setStep = useCallback(
    (newStep: PatientCreationFormState['step']) => {
      setFormState((prevState) => ({
        ...prevState,
        step: newStep,
      }));
    },
    [setFormState],
  );

  const setFormValues = useCallback(
    (newValues: Partial<PatientCreationFormState['values']>) => {
      setFormState((prevState) => ({
        ...prevState,
        isNextStepDisabled: false,
        values: {
          ...prevState.values,
          ...newValues,
        },
      }));
    },
    [setFormState],
  );

  const onModalClose = useCallback(() => {
    closeModal('PatientCreate');
  }, [closeModal]);

  const resetForm = useCallback(() => {
    setFormState({
      values: {},
      step: PatientCreateSteps.PATIENT_INTRO,
    });
  }, [setFormState]);

  const setFormValuesFromFoundPatient = useCallback(
    ({
      id,
      email,
      phone,
      firstName,
      lastName,
      dateOfBirth,
      mrn,
      matchType,
    }: PatientSearchVerifyResponseFound) => {
      setFormValues({
        id,
        email,
        phone,
        firstName,
        lastName,
        dateOfBirth,
        mrn,
        matchType,
      });
    },
    [setFormValues],
  );

  const onPatientIntroSubmit = useCallback(
    async ({ phone }: PatientIntroFormValues) => {
      const { found } = await patientSearchVerifyMutation.mutateAsync({
        phone,
      });

      if (found.length === 0) {
        setFormValues({ phone });
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
      } else if (found.length === 1) {
        const [patient] = found;

        setSelectedFoundPatientId(undefined);

        if (patient?.matchType === 'phone') {
          // Means the patient phone numbers was found in the current department
          setStep(PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND);
          setFoundPatients([patient]);
        } else if (patient?.matchType === 'department') {
          // Means the patient phone numbers was found in another department
          setStep(PatientCreateSteps.PATIENT_ALREADY_EXISTS);
          setFoundPatients([patient]);
          setIsNextStepDisabled(false);
          setFormValuesFromFoundPatient(patient);
        } else if (patient?.matchType === 'archived') {
          // Means the patient phone numbers was found in the current department archive
          // TODO This Workflow need to be updated, in a different issue
          setStep(PatientCreateSteps.PATIENT_ALREADY_EXISTS);
          setFoundPatients([patient]);
          setIsNextStepDisabled(false);
          setFormValuesFromFoundPatient(patient);
        }
      } else {
        setStep(PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND);

        setFoundPatients(found);
        setSelectedFoundPatientId(undefined);
      }
    },
    [
      patientSearchVerifyMutation,
      setFormValues,
      setStep,
      setSelectedFoundPatientId,
      setFoundPatients,
      setIsNextStepDisabled,
      setFormValuesFromFoundPatient,
    ],
  );

  const onPatientInformationSubmit = useCallback(
    (infoFormValues: PatientInformationFormValues) => {
      setFormValues(infoFormValues);
      setStep(PatientCreateSteps.PATIENT_SETTINGS);
    },
    [setStep, setFormValues],
  );

  const handlePatientFoundTableSubmit = useCallback(() => {
    if (!selectedFoundPatientId) {
      return;
    }

    const selectedPatientData = foundPatients?.find(
      (foundPatient) => foundPatient.id === selectedFoundPatientId,
    );

    if (!selectedPatientData) {
      return;
    }

    setFormValuesFromFoundPatient(selectedPatientData);

    setStep(PatientCreateSteps.PATIENT_INFORMATION);
  }, [
    selectedFoundPatientId,
    foundPatients,
    setFormValuesFromFoundPatient,
    setStep,
  ]);

  const handlePatientSettingsSubmit = useCallback(async () => {
    const settingsValues = settingsFormRef.current?.getValues() ?? {};

    setFormValues(settingsValues);

    const allValues = {
      ...values,
      ...settingsValues,
    };

    // TODO: validate allValues before sending to API
    // MR: This is likely subject to change looking at https://newyuinc.atlassian.net/wiki/spaces/LM/pages/4866671142/User+API

    const response = await patientCreateMutation.mutateAsync({
      firstName: allValues.firstName ?? '',
      lastName: allValues.lastName ?? '',
      dateOfBirth: allValues.dateOfBirth ?? '',
      email: allValues.email ?? '',
      phone: allValues.phone ?? '',
      departmentId: '???', // TODO: replace with actual department ID
      country: 'US',
      language: 'en-US',
      zipCode: allValues.zipCode ?? '',
      mrn: allValues.mrn ?? '',
      inviteBy: 'email',
      sendInvite: allValues.sendInvite ?? true,
      settings: {
        displayOn: allValues.displayLactateData ?? true,
        alert: allValues.highLactateAlert
          ? {
              value: allValues.highLactateAlertAbove ?? 0,
              durationMin: allValues.highLactateAlertDuration ?? 0,
            }
          : undefined,
      },
    });

    await queryClient.invalidateQueries({
      queryKey: ['patient-search'],
    });

    // Form reset
    resetForm();
    onModalClose();

    openModal('PatientAdded', {
      patientId: response.id,
      patientName: `${response.firstName} ${response.lastName}`,
      inviteBy: 'email',
      patientEmail: response.email,
      patientPhone: response.phone,
      testID: 'patient-added-modal',
    });
  }, [
    settingsFormRef,
    onModalClose,
    openModal,
    setFormValues,
    values,
    patientCreateMutation,
    queryClient,
    resetForm,
  ]);

  const handlePatientInformationGoBack = useCallback(() => {
    if (!foundPatients) {
      setStep(PatientCreateSteps.PATIENT_INTRO);

      return;
    }

    if (foundPatients.length > 1) {
      setStep(PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND);

      return;
    }

    const [foundPatient] = foundPatients;

    switch (foundPatient.matchType) {
      case 'archived':
      case 'department':
        setStep(PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND);
        break;
      case 'phone':
        setStep(PatientCreateSteps.PATIENT_ALREADY_EXISTS);
        break;
    }
  }, [foundPatients, setStep]);

  const goToPreviousStep = useCallback(() => {
    switch (step) {
      case PatientCreateSteps.PATIENT_INFORMATION:
        handlePatientInformationGoBack();
        break;
      case PatientCreateSteps.PATIENT_EMAIL_ACCOUNT_WAS_FOUND:
      case PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND:
      case PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND:
      case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
        setStep(PatientCreateSteps.PATIENT_INTRO);
        break;
      case PatientCreateSteps.PATIENT_SETTINGS:
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
        break;
      default:
        onModalClose();
    }
  }, [handlePatientInformationGoBack, onModalClose, setStep, step]);

  const goToNextStep = useCallback(async () => {
    switch (step) {
      case PatientCreateSteps.PATIENT_INTRO:
        await introFormRef.current?.submitForm();
        break;
      case PatientCreateSteps.PATIENT_EMAIL_ACCOUNT_WAS_FOUND:
      case PatientCreateSteps.PATIENT_PHONE_ACCOUNT_WAS_FOUND:
      case PatientCreateSteps.PATIENT_MULTIPLE_ACCOUNTS_FOUND:
        handlePatientFoundTableSubmit();
        break;
      case PatientCreateSteps.PATIENT_ALREADY_EXISTS:
        setStep(PatientCreateSteps.PATIENT_INFORMATION);
        break;
      case PatientCreateSteps.PATIENT_INFORMATION:
        await informationFormRef.current?.submitForm();
        break;
      case PatientCreateSteps.PATIENT_SETTINGS:
        await handlePatientSettingsSubmit();
        break;
      default:
        onModalClose();
        break;
    }
  }, [
    step,
    introFormRef,
    handlePatientFoundTableSubmit,
    setStep,
    informationFormRef,
    handlePatientSettingsSubmit,
    onModalClose,
  ]);

  // Flags that causing loading behavior for the form
  const isFormLoading =
    patientSearchVerifyMutation.isPending || patientCreateMutation.isPending;

  return {
    values,
    isFormLoading,
    currentStep: step,
    isNextStepDisabled,
    foundPatients,
    selectedFoundPatientId,
    selectFoundPatient,
    setIsNextStepDisabled,
    goToPreviousStep,
    goToNextStep,
    onModalClose,
    onPatientIntroSubmit,
    onPatientInformationSubmit,
    resetForm,
  };
};
